import { FreestyleMethodState } from '../../../types';

/**
 * Returns the number of selected cells in the given content
 * @param freestyleContent - The freestyle cell data
 */
export const countSelectedCells = (freestyleContent: FreestyleMethodState) => {
  return Object.keys(freestyleContent).reduce((acc, curr) => {
    return acc + freestyleContent[curr].filter(cell => cell.selected).length;
  }, 0);
};

/**
 * Decrease the selected cell data by the given amount
 * @param freestyleContent - The freestyle cell data
 * @param value - The amount to decrease the selected cells by
 */
export const decreaseByAmount = (freestyleContent: FreestyleMethodState, value: string) => {
  const newContent: FreestyleMethodState = {};

  Object.keys(freestyleContent).map(year => {
    const noSelectedCells = !freestyleContent[year].filter(cell => cell.selected).length;

    if (noSelectedCells) {
      newContent[year] = freestyleContent[year];
    } else {
      const yearData = freestyleContent[year].map(d => {
        if (d.selected) {
          return { ...d, value: String(Number(d.value || '0') - Number(value)) };
        }
        return d;
      });
      newContent[year] = yearData;
    }
  });

  return newContent;
};

/**
 * Decrease the selected cell data by the given % value
 * @param freestyleContent - The freestyle cell data
 * @param value - The % value to decrease the selected cells by
 */
export const decreaseByPercentage = (freestyleContent: FreestyleMethodState, value: string) => {
  const newContent: FreestyleMethodState = {};

  Object.keys(freestyleContent).map(year => {
    const noSelectedCells = !freestyleContent[year].filter(cell => cell.selected).length;

    if (noSelectedCells) {
      newContent[year] = freestyleContent[year];
    } else {
      const yearData = freestyleContent[year].map(d => {
        if (d.selected) {
          const valueStr = String(Number(d.value || '0') - Number(d.value || '0') * (Number(value) / 100));
          const valueNumRounded = Math.round((Number(valueStr) + Number.EPSILON) * 100) / 100;
          return { ...d, value: String(valueNumRounded) };
        }
        return d;
      });
      newContent[year] = yearData;
    }
  });

  return newContent;
};

/**
 * Returns true if the given freestyleContent has one or more selected cells
 * @param freestyleContent - The content to check for selected cells
 */
export const hasSelectedCells = (freestyleContent: FreestyleMethodState) => {
  return !!Object.keys(freestyleContent).find(year => {
    return freestyleContent[year].find(cell => cell.selected);
  });
};

/**
 * Returns true if the given freestyleContent has one or more selected cells which also have a value
 * @param freestyleContent - The content to check for selected cells with values
 */
export const hasSelectedCellsWithValues = (freestyleContent: FreestyleMethodState) => {
  return !!Object.keys(freestyleContent).find(year => {
    return freestyleContent[year].find(cell => cell.selected && cell.value);
  });
};

/**
 * Increase the selected cell data by the given amount
 * @param freestyleContent - The freestyle cell data
 * @param value - The amount to increase the selected cells by
 */
export const increaseByAmount = (freestyleContent: FreestyleMethodState, value: string) => {
  const newContent: FreestyleMethodState = {};

  Object.keys(freestyleContent).map(year => {
    const noSelectedCells = !freestyleContent[year].filter(cell => cell.selected).length;

    if (noSelectedCells) {
      newContent[year] = freestyleContent[year];
    } else {
      const yearData = freestyleContent[year].map(d => {
        if (d.selected) {
          return {
            ...d,
            value: String(Number(d.value || '0') + Number(value))
          };
        }
        return d;
      });
      newContent[year] = yearData;
    }
  });

  return newContent;
};

/**
 * Increase the selected cell data by the given % value
 * @param freestyleContent - The freestyle cell data
 * @param value - The % value to increase the selected cells by
 */
export const increaseByPercentage = (freestyleContent: FreestyleMethodState, value: string) => {
  const newContent: FreestyleMethodState = {};

  Object.keys(freestyleContent).map(year => {
    const noSelectedCells = !freestyleContent[year].filter(cell => cell.selected).length;

    if (noSelectedCells) {
      newContent[year] = freestyleContent[year];
    } else {
      const yearData = freestyleContent[year].map(d => {
        if (d.selected) {
          const valueStr = String(Number(d.value || '0') + Number(d.value || '0') * (Number(value) / 100));
          const valueNumRounded = Math.round((Number(valueStr) + Number.EPSILON) * 100) / 100;
          return {
            ...d,
            value: String(valueNumRounded)
          };
        }
        return d;
      });
      newContent[year] = yearData;
    }
  });

  return newContent;
};

/**
 * Set the selected cell data to the given value
 * @param freestyleContent - The freestyle cell data
 * @param value - The value to set the selected cells to
 */
export const setSelectedTo = (freestyleContent: FreestyleMethodState, value: string) => {
  const newContent: FreestyleMethodState = {};

  Object.keys(freestyleContent).map(year => {
    const noSelectedCells = !freestyleContent[year].filter(cell => cell.selected).length;

    if (noSelectedCells) {
      newContent[year] = freestyleContent[year];
    } else {
      const yearData = freestyleContent[year].map(d => {
        return d.selected ? { ...d, value } : d;
      });
      newContent[year] = yearData;
    }
  });

  return newContent;
};

/**
 * Takes the given amount, divides it by the number of selected cells and sets each selected cell value to that value
 * @param freestyleContent - The freestyle cell data
 * @param value - The value to split between the selected cells to
 */
export const splitBetweenSelected = (freestyleContent: FreestyleMethodState, value: string) => {
  const newContent: FreestyleMethodState = {};
  const selectedCells = countSelectedCells(freestyleContent);
  const newValue = String(Math.round((Number(value) / selectedCells + Number.EPSILON) * 100) / 100);

  Object.keys(freestyleContent).map(year => {
    const noSelectedCells = !freestyleContent[year].filter(cell => cell.selected).length;

    if (noSelectedCells) {
      newContent[year] = freestyleContent[year];
    } else {
      const yearData = freestyleContent[year].map(d => {
        return d.selected ? { ...d, value: newValue } : d;
      });
      newContent[year] = yearData;
    }
  });

  return newContent;
};
